import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { ChatMessage, ReferenceDocument, CocoCelebrationService } from '@coin/chat-bot-util-types';
import { V2ButtonComponent, EmployeePictureComponent } from '@coin/shared/core/ui-storybook';
import { MarkdownComponent } from 'ngx-markdown';
import { DocumentReferenceCardComponent } from '../document-reference-card/document-reference-card.component';
import { MessageToolbarAction, MessageToolbarComponent } from '../message-toolbar/message-toolbar.component';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'coin-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrl: './chat-message.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MarkdownComponent,
    DocumentReferenceCardComponent,
    V2ButtonComponent,
    EmployeePictureComponent,
    MessageToolbarComponent,
    TranslateModule
  ]
})
export class ChatMessageComponent {
  private toast = inject(ToastrService);
  private translate = inject(TranslateService);
  private celebrationManager = inject(CocoCelebrationService);

  message = input.required<ChatMessage>();
  isUserMessage = computed(() => this.message().owner === 'user');
  download = output<ReferenceDocument>();
  sourcesOpen = signal<boolean>(false);
  gid = input<string>('');

  protected readonly botAvatarPath = signal<string | null>(this.celebrationManager.getAvatar());
  protected readonly botAvatarIsPng = computed<boolean>(() => {
    const path = this.botAvatarPath();
    return path ? path.toLowerCase().endsWith('.png') : false;
  });

  toggleSources() {
    this.sourcesOpen.set(!this.sourcesOpen());
  }

  messageActions = content =>
    computed<MessageToolbarAction[]>(() => {
      return [
        {
          icon: 'content_copy',
          tooltip: this.translate.instant('chat-bot.tooltip.copy-message'),
          action: () => this.copyContent(content)
        }
      ];
    });

  copyContent(content: string): void {
    navigator.clipboard
      .writeText(content?.trim())
      .then(() => {
        this.toast.info(this.translate.instant('chat-bot.actions.copy-success'));
      })
      .catch(err => {
        this.toast.error(this.translate.instant('chat-bot.actions.copy-failure'));
        console.error('Error copying content: ', err);
      });
  }
}
